import { execa } from 'execa';
import { logger } from './logger';

export interface RipgrepOptions {
  pattern: string;
  path?: string;
  fileType?: string;
  maxCount?: number;
  ignoreCase?: boolean;
  followSymlinks?: boolean;
  excludeGlobs?: string[];
}

export interface RipgrepResult {
  file: string;
  line: number;
  column: number;
  match: string;
  context?: string;
}

export class RipgrepSearch {
  private isAvailable: boolean | null = null;

  async checkAvailability(): Promise<boolean> {
    if (this.isAvailable !== null) {
      return this.isAvailable;
    }

    try {
      await execa('rg', ['--version']);
      this.isAvailable = true;
      logger.debug('ripgrep is available');
    } catch {
      this.isAvailable = false;
      logger.debug('ripgrep is not available, falling back to JavaScript search');
    }

    return this.isAvailable;
  }

  async search(options: RipgrepOptions): Promise<RipgrepResult[]> {
    if (!await this.checkAvailability()) {
      return this.fallbackSearch(options);
    }

    const args = this.buildArgs(options);
    
    try {
      const { stdout } = await execa('rg', args, {
        cwd: options.path || process.cwd(),
        maxBuffer: 10 * 1024 * 1024 // 10MB buffer
      });

      return this.parseOutput(stdout);
    } catch (error: any) {
      if (error.exitCode === 1) {
        // No matches found
        return [];
      }
      throw error;
    }
  }

  private buildArgs(options: RipgrepOptions): string[] {
    const args = [
      '--json', // JSON output for easy parsing
      '--no-heading',
      '--with-filename',
      '--line-number',
      '--column'
    ];

    if (options.ignoreCase) {
      args.push('-i');
    }

    if (options.followSymlinks) {
      args.push('-L');
    }

    if (options.maxCount) {
      args.push('-m', options.maxCount.toString());
    }

    if (options.fileType) {
      args.push('-t', options.fileType);
    }

    if (options.excludeGlobs) {
      options.excludeGlobs.forEach(glob => {
        args.push('-g', `!${glob}`);
      });
    }

    // Add default excludes
    args.push('-g', '!node_modules');
    args.push('-g', '!.git');
    args.push('-g', '!dist');
    args.push('-g', '!coverage');

    // Pattern must be last
    args.push(options.pattern);

    return args;
  }

  private parseOutput(output: string): RipgrepResult[] {
    const results: RipgrepResult[] = [];
    const lines = output.split('\n').filter(line => line.trim());

    for (const line of lines) {
      try {
        const data = JSON.parse(line);
        
        if (data.type === 'match') {
          const match = data.data;
          results.push({
            file: match.path.text,
            line: match.line_number,
            column: match.submatches[0]?.start || 0,
            match: match.lines.text.trim(),
            context: match.lines.text
          });
        }
      } catch (error) {
        // Skip invalid JSON lines
      }
    }

    return results;
  }

  private async fallbackSearch(options: RipgrepOptions): Promise<RipgrepResult[]> {
    // Simple JavaScript fallback implementation
    const fs = await import('fs-extra');
    const path = await import('path');
    const glob = await import('glob');
    
    const searchPath = options.path || process.cwd();
    const pattern = options.ignoreCase 
      ? new RegExp(options.pattern, 'gi')
      : new RegExp(options.pattern, 'g');
    
    // Get files to search
    const files = await glob.glob('**/*', {
      cwd: searchPath,
      ignore: ['node_modules/**', '.git/**', 'dist/**', ...(options.excludeGlobs || [])],
      nodir: true
    });

    const results: RipgrepResult[] = [];
    let matchCount = 0;

    for (const file of files) {
      if (options.maxCount && matchCount >= options.maxCount) {
        break;
      }

      const filePath = path.join(searchPath, file);
      
      try {
        const content = await fs.readFile(filePath, 'utf-8');
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          const matches = line.matchAll(pattern);
          
          for (const match of matches) {
            if (options.maxCount && matchCount >= options.maxCount) {
              break;
            }
            
            results.push({
              file: filePath,
              line: i + 1,
              column: match.index || 0,
              match: line.trim(),
              context: line
            });
            
            matchCount++;
          }
        }
      } catch (error) {
        // Skip files that can't be read
      }
    }

    return results;
  }
}